import React, { useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import NotificationsBell from '@/components/portal/Notifications';
import {
  LayoutDashboard, FolderKanban, FileSpreadsheet, ShoppingCart, Receipt, Clock, LineChart, Users, FileBarChart,
  Settings, LogOut, Truck, CheckSquare, Library, NotebookPen, FileStack, Wallet, HandCoins, Layers, ClipboardList,
  UserCog, Landmark, ChevronDown, FileText, HardHat, ListTodo, ScanSearch } from 'lucide-react';
import { TEST_IDS } from '@/constants/testIds/alvora';
import { useAuth } from '@/context/AuthContext';
import { useFinanceAuthority } from '@/lib/finance/invoices';
import { BrandLockup } from '@/components/BrandLogo';

const MANAGER = ['owner', 'admin', 'manager'];

// Flat list in the original order. `roles` restricts visibility only; pages still enforce their own access.
const NAV = [
  { to: '/dashboard', label: 'Command Centre', icon: LayoutDashboard, tid: TEST_IDS.navDashboard },
  { to: '/projects', label: 'Projects', icon: FolderKanban, tid: TEST_IDS.navProjects },
  { to: '/estimating', label: 'Estimating & BOQs', icon: FileSpreadsheet, tid: TEST_IDS.navEstimating },
  { to: '/suppliers', label: 'Suppliers', icon: Truck, tid: 'nav-suppliers' },
  { to: '/approvals', label: 'Approvals', icon: CheckSquare, tid: 'nav-approvals' },
  { to: '/expenses/approvals', label: 'Expense Approvals', icon: Wallet, tid: 'nav-expense-approvals', roles: MANAGER },
  { to: '/purchasing', label: 'Purchasing', icon: ShoppingCart, tid: TEST_IDS.navPurchasing },
  { to: '/costing', label: 'Costing Library', icon: Library, tid: 'nav-costing' },
  { to: '/library/buildups/import', label: 'Build-Up Library', icon: Layers, tid: 'nav-buildups', roles: MANAGER },
  { to: '/invoices', label: 'Invoice Inbox', icon: Receipt, tid: TEST_IDS.navInvoices, roles: MANAGER },
  { to: '/labour', label: 'Labour & Timesheets', icon: Clock, tid: TEST_IDS.navLabour },
  { to: '/me/expenses', label: 'My Expenses', icon: HandCoins, tid: 'nav-my-expenses' },
  { to: '/me/work', label: 'My Work', icon: ClipboardList, tid: 'nav-my-work' },
  { to: '/tasks', label: 'To-Do', icon: ListTodo, tid: 'nav-tasks', roles: ['owner'] },
  { to: '/site-diary', label: 'Site Diary', icon: NotebookPen, tid: 'nav-site-diary' },
  { to: '/drawings', label: 'Drawings', icon: FileStack, tid: 'nav-drawings' },
  // SS-1: the whole Snag Surveyors division lives under this one entry (internal navigation inside the section).
  // Hidden in Production until SS-1 is completed and approved (Owner decision, CP-2 release); routes/code remain intact.
  ...(((process.env.REACT_APP_SUPABASE_URL || '').includes('lmvtyigaiqyfqxfmrjkx')) ? [] : [{ to: '/snag', label: 'Snag Surveyors', icon: ScanSearch, tid: 'nav-snag' }]),
  // Finance group: all financial sections live here. Only implemented sections are listed; each child keeps its own role gate.
  { group: 'finance', label: 'Finance', icon: Landmark, tid: 'nav-finance-group', children: [
    { to: '/finance/clients', label: 'Clients', icon: Users, tid: 'nav-finance-clients', roles: MANAGER, finance: true },
    { to: '/finance/quotations', label: 'Quotations', icon: FileText, tid: 'nav-finance-quotations', roles: ['owner', 'admin'], finance: true },
    { to: '/finance/invoices', label: 'Client Invoices', icon: FileText, tid: 'nav-finance-invoices', roles: ['owner', 'admin'], finance: true },
    { to: '/financial', label: 'Financial Control', icon: LineChart, tid: TEST_IDS.navFinancial, roles: MANAGER },
    { to: '/finance/subcontractors', label: 'Subcontractor Payments', icon: HardHat, tid: 'nav-finance-subcontractors', roles: ['owner', 'admin'], finance: true },
    { to: '/finance/records', label: 'Finance Records', icon: FileBarChart, tid: 'nav-finance-records', roles: ['owner', 'admin'] },
  ] },
  { to: '/labour?tab=employees', label: 'Employee Register', icon: Users, tid: TEST_IDS.navStaff, roles: MANAGER },
  { to: '/settings/team', label: 'Team & Access', icon: UserCog, tid: 'nav-team', roles: MANAGER },
  { to: '/settings', label: 'Company Settings', icon: Settings, tid: TEST_IDS.navSettings, roles: MANAGER },
];

const split = (to) => { const [path, qs] = to.split('?'); return { path, tab: qs ? new URLSearchParams(qs).get('tab') : null }; };
// `finance: true` entries are also shown to explicitly authorised finance users (finance_access), whatever their role.
const visible = (entry, role, financeUser) => !entry.roles || entry.roles.includes(role || '') || (entry.finance && financeUser);

function isActive(item, location, siblings) {
  const { path, tab } = split(item.to);
  if (!(location.pathname === path || location.pathname.startsWith(path + '/'))) return false;
  const curTab = new URLSearchParams(location.search).get('tab');
  if (tab) return curTab === tab;
  const claimed = siblings.map((s) => split(s.to)).filter((s) => s.path === path && s.tab).map((s) => s.tab);
  return !claimed.includes(curTab);
}

function NavItem({ item, active, onNavigate, nested }) {
  const Icon = item.icon;
  return (
    <Link to={item.to} data-testid={item.tid} onClick={onNavigate} aria-current={active ? 'page' : undefined}
      className={`group relative flex items-center gap-3 rounded-md py-2 text-sm transition-colors duration-150 ${nested ? 'pl-9 pr-3' : 'px-3'} ${active ? 'bg-ivory/[0.08] text-ivory' : 'text-ivory/75 hover:bg-ivory/[0.05] hover:text-ivory'}`}>
      <span className={`absolute left-0 top-2 bottom-2 w-[2px] rounded-r-full transition-colors duration-150 ${active ? 'bg-[#957C5D]' : 'bg-transparent'}`} aria-hidden="true" />
      <Icon size={nested ? 16 : 18} strokeWidth={1.5} className={active ? 'text-[#D8D0C4]' : 'text-ivory/55'} />
      <span className="truncate">{item.label}</span>
    </Link>
  );
}

function NavGroup({ group, location, onNavigate }) {
  const key = `alvora.nav.${group.group}`;
  const childActive = group.children.some((c) => isActive(c, location, group.children));
  const [open, setOpen] = useState(() => childActive || localStorage.getItem(key) !== '0');
  const expanded = open || childActive;
  const toggle = () => { const next = !expanded; setOpen(next); localStorage.setItem(key, next ? '1' : '0'); };
  const Icon = group.icon;
  return (
    <div data-testid={group.tid}>
      <button type="button" onClick={toggle} aria-expanded={expanded} data-testid={`${group.tid}-toggle`}
        className={`group relative w-full flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors duration-150 ${childActive ? 'text-ivory' : 'text-ivory/75 hover:bg-ivory/[0.05] hover:text-ivory'}`}>
        <span className={`absolute left-0 top-2 bottom-2 w-[2px] rounded-r-full transition-colors duration-150 ${childActive && !expanded ? 'bg-[#957C5D]' : 'bg-transparent'}`} aria-hidden="true" />
        <Icon size={18} strokeWidth={1.5} className={childActive ? 'text-[#D8D0C4]' : 'text-ivory/55'} />
        <span className="flex-1 text-left truncate">{group.label}</span>
        <ChevronDown size={14} className={`text-ivory/50 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`} />
      </button>
      {expanded && <div className="space-y-0.5" data-testid={`${group.tid}-items`}>
        {group.children.map((c) => <NavItem key={c.to} item={c} nested active={isActive(c, location, group.children)} onNavigate={onNavigate} />)}
      </div>}
    </div>
  );
}

export default function Sidebar({ onNavigate }) {
  const { user, organisation, role, signOut } = useAuth();
  const location = useLocation();
  const initials = (organisation?.name || user?.email || 'A').split(/\s+/).map((s) => s[0]).join('').slice(0, 2).toUpperCase();

  // Groups keep only the children the role may see and disappear entirely when none remain.
  const financeUser = useFinanceAuthority().canView;
  const nav = useMemo(() => NAV
    .map((e) => e.group ? { ...e, children: e.children.filter((c) => visible(c, role, financeUser)) } : e)
    .filter((e) => e.group ? e.children.length > 0 : visible(e, role, financeUser)), [role, financeUser]);

  return (
    <aside data-testid={TEST_IDS.sidebar} className="h-full w-64 bg-slate-sidebar text-ivory flex flex-col" style={{ backgroundColor: '#3A3936' }}>
      <div className="px-5 pt-6 pb-5 border-b border-ivory/10">
        <Link to="/dashboard" onClick={onNavigate} data-testid="sidebar-brand-link" className="inline-flex">
          <BrandLockup tone="white" markSize={38} textClass="text-ivory" />
        </Link>
      </div>

      <nav className="flex-1 overflow-y-auto alvora-scroll px-3 py-4 space-y-0.5">
        {nav.map((e) => e.group
          ? <NavGroup key={e.group} group={e} location={location} onNavigate={onNavigate} />
          : <NavItem key={e.to} item={e} active={isActive(e, location, nav.filter((s) => s.to))} onNavigate={onNavigate} />)}
      </nav>

      <div className="border-t border-ivory/10 p-3">
        <div className="flex items-center gap-3 rounded-md px-2 py-2">
          <div className="h-9 w-9 rounded-full border border-[#957C5D]/60 text-[#D8D0C4] flex items-center justify-center text-xs font-medium">{initials}</div>
          <div className="flex-1 min-w-0">
            <div className="text-sm text-ivory truncate">{organisation?.name || 'No organisation'}</div>
            <div className="text-[11px] uppercase tracking-[0.14em] text-[#B5AFA5] truncate">{user?.email}{role ? ` · ${role}` : ''}</div>
          </div>
          <NotificationsBell dark />
          <button data-testid={TEST_IDS.logoutButton} onClick={signOut} title="Sign out" aria-label="Sign out"
            className="h-8 w-8 inline-flex items-center justify-center rounded-md text-ivory/60 hover:text-ivory hover:bg-ivory/10 transition-colors duration-150">
            <LogOut size={16} />
          </button>
        </div>
      </div>
    </aside>
  );
}
